import { ReactNode, useState, useEffect } from "react";
import { Navbar } from "@/components/navbar";
import { DashboardSidebar } from "./dashboard-sidebar";
import { RefactoredAgentChatSidebar } from "@/components/agent/RefactoredAgentChatSidebar";
import { useAgentSidebar } from "@/contexts/AgentSidebarContext";
import { useFocusMode } from "@/contexts/FocusModeContext";
import { useAgentCanvas } from "@/contexts/AgentCanvasContext";
import { GenerativeCanvas } from "@/components/agent/GenerativeCanvas";

interface DashboardLayoutProps {
  children: ReactNode;
  activeTab?: string;
  showAgentSidebar?: boolean;
  hideSidebar?: boolean; // If true, hide main navigation sidebar completely
  agentSidebarTitle?: string; // Custom title for the agent sidebar
  agentSidebarSubtitle?: string; // Custom subtitle for the agent sidebar
  filterThreadsWithDocuments?: boolean; // If true, only show threads with associated documents
  agentSidebarShowHistory?: boolean; // If true, show history view instead of chat view
}

export function DashboardLayout({
  children,
  activeTab = "overview",
  showAgentSidebar = true,
  hideSidebar = false,
  agentSidebarTitle,
  agentSidebarSubtitle,
  filterThreadsWithDocuments,
  agentSidebarShowHistory,
}: DashboardLayoutProps) {
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const { isVisible: isAgentSidebarVisible, isMinimized: isAgentSidebarMinimized } =
    useAgentSidebar();
  const { hasAnyFocusedConversation } = useFocusMode();
  const { isOpen: isCanvasOpen, htmlContent, isStreaming } = useAgentCanvas();

  const shouldHideSidebar = hideSidebar || activeTab === "agent-mode";

  // Listen for sidebar toggle events from the navbar
  useEffect(() => {
    const handleToggleSidebar = () => {
      setShowMobileSidebar((prev) => !prev);
    };

    document.addEventListener("toggle-mobile-sidebar", handleToggleSidebar);
    return () => {
      document.removeEventListener("toggle-mobile-sidebar", handleToggleSidebar);
    };
  }, []);

  return (
    <div
      className={`flex min-h-screen flex-col bg-gray-50 sm:flex-row ${
        hasAnyFocusedConversation ? "focus-mode-active" : ""
      }`}
    >
      {/* Navigation Sidebar - Hidden in focus mode or when hideSidebar is set */}
      {!shouldHideSidebar && (
        <>
          {/* Overlay for mobile */}
          <div
            className={`${showMobileSidebar ? "fixed inset-0 z-30 bg-black/50" : "hidden"} sm:hidden`}
            onClick={() => setShowMobileSidebar(false)}
          />
          {!hasAnyFocusedConversation && (
            <aside
              className={`${showMobileSidebar ? "fixed inset-0 z-40 block" : "hidden"} h-screen sm:relative sm:sticky sm:top-0 sm:block sm:z-50`}
            >
              <div className="h-full">
                <DashboardSidebar />
              </div>
            </aside>
          )}
        </>
      )}

      {/* Main content area */}
      <div className="flex w-full flex-1 flex-col">
        {!hasAnyFocusedConversation && <Navbar />}

        {/* Content with optional agent sidebar */}
        <div className="flex flex-1">
          {/* Main content - automatically responsive, full width in focus mode */}
          <main
            className={`relative flex-1 transition-all duration-500 ease-in-out ${
              hasAnyFocusedConversation
                ? "p-0" // No padding in focus mode
                : `p-4 sm:p-6 ${
                    showAgentSidebar && isAgentSidebarVisible
                      ? "lg:mr-96" // Add right margin when sidebar is visible
                      : ""
                  }`
            }`}
          >
            {/* 
              If the Agent Canvas is open, it takes over the main view.
              We keep children mounted but hidden so state isn't lost. 
            */}
            {isCanvasOpen && (
              <div className="absolute inset-4 z-10">
                <GenerativeCanvas
                  htmlContent={htmlContent}
                  isStreaming={isStreaming}
                  className="h-full border-0 shadow-lg"
                />
              </div>
            )}
            <div className={isCanvasOpen ? "hidden" : "block h-full"}>{children}</div>
          </main>

          {/* Agent Chat Sidebar - Desktop and tablet responsive */}
          {showAgentSidebar && isAgentSidebarVisible && !hasAnyFocusedConversation && (
            <div className="fixed right-0 top-16 z-40 h-[calc(100vh-4rem)] w-full max-w-sm sm:w-96 shadow-2xl lg:shadow-lg border-l border-gray-200 bg-white">
              <RefactoredAgentChatSidebar
                fillParent={true}
                title={agentSidebarTitle}
                subtitle={agentSidebarSubtitle}
                filterThreadsWithDocuments={filterThreadsWithDocuments}
                forceShowHistory={agentSidebarShowHistory}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
